import { db } from '../config/firebase.ts';
import { collection, doc, getDocs, query, where, orderBy, limit, Timestamp, addDoc, serverTimestamp } from 'firebase/firestore';
import { getTrackingInfo } from '../utils/trackingUtils.ts';
import { getDisplayName } from '../utils/userUtils';
import { 
  AdminActivity, 
  LoginActivity, 
  MarketplaceActivity, 
  PostActivity, 
  UserActivity,
  AdminActivityFilter,
  PaginationConfig,
  AdminActivityResponse
} from '../models/AdminActivity';
import { AdminActivityApiService } from '../services/adminActivityApi.ts';

// In-memory cache for activities (will be synced with API)
let adminActivities: AdminActivity[] = [];
let activitiesLoaded = false;

// Load activities directly from Firestore
const loadActivities = async (): Promise<void> => {
  if (!activitiesLoaded) {
    try {
      console.log('Loading admin activities directly from Firestore...');
      const activitiesRef = collection(db, 'admin_activities');
      const q = query(activitiesRef, orderBy('timestamp', 'desc'), limit(1000));
      const snapshot = await getDocs(q);
      
      adminActivities = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.()?.toISOString() || new Date().toISOString()
        };
      }) as AdminActivity[];
      
      console.log(`Loaded ${adminActivities.length} admin activities from Firestore`);
      activitiesLoaded = true;
    } catch (error) {
      console.error('Failed to load admin activities from Firestore:', error);
      // Fallback to API service
      try {
        console.log('Falling back to API service...');
        adminActivities = await AdminActivityApiService.getAllActivities();
        activitiesLoaded = true;
      } catch (apiError) {
        console.error('Failed to load admin activities from API service:', apiError);
        adminActivities = [];
        activitiesLoaded = true;
      }
    }
  }
};

// Core activity logging functions
// Track recent login activities to prevent duplicates
const recentLoginActivities = new Set<string>();
const DUPLICATE_PREVENTION_WINDOW = 5000; // 5 seconds

export const logLoginActivity = async (
  userId: string,
  userName: string,
  success: boolean,
  failureReason?: string
): Promise<void> => {
  // Create a unique key for this login attempt to prevent duplicates
  const activityKey = `${userId}-${userName}-${success}-${Date.now().toString().slice(0, -4)}`;
  
  // Check if we already logged this recently (prevents session restoration spam)
  if (recentLoginActivities.has(activityKey)) {
    console.log('🚫 Skipping duplicate login activity for:', userName);
    return;
  }
  
  // Add to recent activities tracker
  recentLoginActivities.add(activityKey);
  
  // Clean up old entries after the prevention window
  setTimeout(() => {
    recentLoginActivities.delete(activityKey);
  }, DUPLICATE_PREVENTION_WINDOW);
  
  try {
    // Use faster tracking method - just get user agent immediately
    const userAgent = navigator.userAgent;
    
    const activity: any = {
      type: 'login' as const,
      userId,
      userName,
      success,
      ipAddress: 'Unknown', // Will be filled by server-side logging
      location: {
        country: 'Unknown',
        countryCode: 'Unknown',
        city: 'Unknown',
        region: 'Unknown',
        timezone: 'Unknown',
        isp: 'Unknown',
        org: 'Unknown'
      },
      userAgent: userAgent,
      details: success 
        ? `User logged in successfully` 
        : `Login failed: ${failureReason || 'Invalid credentials'}`
    };
    
    // Only add failureReason if it exists (for failed logins)
    if (failureReason) {
      activity.failureReason = failureReason;
    }
    
    // Add directly to Firestore
    const activityWithTimestamp = {
      ...activity,
      timestamp: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'admin_activities'), activityWithTimestamp);
    const savedActivity = {
      id: docRef.id,
      ...activity,
      timestamp: new Date().toISOString()
    };
    
    // Update local cache
    adminActivities.unshift(savedActivity);
  } catch (error) {
    console.error('Failed to log login activity:', error);
  }
};

export const logMarketplaceActivity = async (
  userId: string,
  userName: string,
  action: MarketplaceActivity['action'],
  entityId: string,
  entityType: MarketplaceActivity['entityType'],
  details: MarketplaceActivity['details']
): Promise<void> => {
  try {
    // Use faster tracking method - just get user agent immediately
    const userAgent = navigator.userAgent;
    
    const activity = {
      type: 'marketplace' as const,
      userId,
      userName,
      action,
      entityId,
      entityType,
      ipAddress: 'Unknown', // Will be filled by server-side logging
      location: {
        country: 'Unknown',
        countryCode: 'Unknown',
        city: 'Unknown',
        region: 'Unknown',
        timezone: 'Unknown',
        isp: 'Unknown',
        org: 'Unknown'
      },
      userAgent: userAgent,
      details
    };
    
    // Add directly to Firestore
    const activityWithTimestamp = {
      ...activity,
      timestamp: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'admin_activities'), activityWithTimestamp);
    const savedActivity = {
      id: docRef.id,
      ...activity,
      timestamp: new Date().toISOString()
    };
    
    // Update local cache
    adminActivities.unshift(savedActivity);
  } catch (error) {
    console.error('Failed to log marketplace activity:', error);
  }
};

export const logPostActivity = async (
  userId: string,
  userName: string,
  action: PostActivity['action'],
  postType: PostActivity['postType'],
  postId: string,
  postTitle: string | undefined,
  details: PostActivity['details']
): Promise<void> => {
  try {
    // Use faster tracking method - just get user agent immediately
    const userAgent = navigator.userAgent;
    
    const activity = {
      type: 'post' as const,
      userId,
      userName,
      action,
      postType,
      postId,
      postTitle,
      ipAddress: 'Unknown', // Will be filled by server-side logging
      location: {
        country: 'Unknown',
        countryCode: 'Unknown',
        city: 'Unknown',
        region: 'Unknown',
        timezone: 'Unknown',
        isp: 'Unknown',
        org: 'Unknown'
      },
      userAgent: userAgent,
      details: `${action} ${postType} - ${postTitle || 'Untitled'}`
    };
    
    const savedActivity = await AdminActivityApiService.addActivity(activity);
    if (savedActivity) {
      // Update local cache
      adminActivities.unshift(savedActivity);
    }
  } catch (error) {
    console.error('Failed to log post activity:', error);
  }
};

export const logUserActivity = async (
  userId: string,
  userName: string,
  action: UserActivity['action'],
  targetUserId: string,
  targetUserName: string,
  details: UserActivity['details']
): Promise<void> => {
  try {
    // Use faster tracking method - just get user agent immediately
    const userAgent = navigator.userAgent;
    
    const activity = {
      type: 'user' as const,
      userId,
      userName,
      action,
      targetUserId,
      targetUserName,
      ipAddress: 'Unknown', // Will be filled by server-side logging
      location: {
        country: 'Unknown',
        countryCode: 'Unknown',
        city: 'Unknown',
        region: 'Unknown',
        timezone: 'Unknown',
        isp: 'Unknown',
        org: 'Unknown'
      },
      userAgent: userAgent,
      details: `${action} user ${targetUserName} - ${details.changedFields ? details.changedFields.join(', ') : 'N/A'}`
    };
    
    const savedActivity = await AdminActivityApiService.addActivity(activity);
    if (savedActivity) {
      // Update local cache
      adminActivities.unshift(savedActivity);
    }
  } catch (error) {
    console.error('Failed to log user activity:', error);
  }
};

// Activity retrieval functions
export const getActivities = async (
  filters: AdminActivityFilter = {},
  page: number = 1,
  pageSize: 25 | 50 | 100 | 250 | 500 | 1000 | 'all' = 25
): Promise<AdminActivityResponse> => {
  // Load activities from API if not loaded
  await loadActivities();
  
  let filteredActivities = [...adminActivities];
  
  // Apply filters
  if (filters.type && filters.type !== 'all') {
    filteredActivities = filteredActivities.filter(activity => activity.type === filters.type);
  }
  
  if (filters.userId) {
    filteredActivities = filteredActivities.filter(activity => activity.userId === filters.userId);
  }
  
  if (filters.dateFrom) {
    const fromDate = new Date(filters.dateFrom);
    filteredActivities = filteredActivities.filter(activity => 
      new Date(activity.timestamp) >= fromDate
    );
  }
  
  if (filters.dateTo) {
    const toDate = new Date(filters.dateTo);
    filteredActivities = filteredActivities.filter(activity => 
      new Date(activity.timestamp) <= toDate
    );
  }
  
  if (filters.action) {
    filteredActivities = filteredActivities.filter(activity => 
      'action' in activity && activity.action === filters.action
    );
  }
  
  // Calculate pagination
  const totalItems = filteredActivities.length;
  let paginatedActivities: AdminActivity[];
  let actualPageSize: number;
  let totalPages: number;
  
  if (pageSize === 'all') {
    paginatedActivities = filteredActivities;
    actualPageSize = totalItems;
    totalPages = 1;
  } else {
    actualPageSize = pageSize;
    totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    paginatedActivities = filteredActivities.slice(startIndex, endIndex);
  }
  
  return {
    activities: paginatedActivities,
    pagination: {
      page,
      pageSize: actualPageSize,
      totalItems,
      totalPages
    },
    filters
  };
};

export const getLoginActivities = async (page: number = 1, pageSize: 25 | 50 | 100 | 250 | 500 | 1000 | 'all' = 25) => {
  return await getActivities({ type: 'login' }, page, pageSize);
};

export const getMarketplaceActivities = async (page: number = 1, pageSize: 25 | 50 | 100 | 250 | 500 | 1000 | 'all' = 25) => {
  return await getActivities({ type: 'marketplace' }, page, pageSize);
};

export const getPostActivities = async (page: number = 1, pageSize: 25 | 50 | 100 | 250 | 500 | 1000 | 'all' = 25) => {
  return await getActivities({ type: 'post' }, page, pageSize);
};

export const getUserActivities = async (page: number = 1, pageSize: 25 | 50 | 100 | 250 | 500 | 1000 | 'all' = 25) => {
  return await getActivities({ type: 'user' }, page, pageSize);
};

// Utility functions
export const getActivityStatistics = async () => {
  await loadActivities();
  
  const loginCount = adminActivities.filter(a => a.type === 'login').length;
  const marketplaceCount = adminActivities.filter(a => a.type === 'marketplace').length;
  const postCount = adminActivities.filter(a => a.type === 'post').length;
  const userCount = adminActivities.filter(a => a.type === 'user').length;
  
  return {
    totalActivities: adminActivities.length,
    loginActivities: loginCount,
    marketplaceActivities: marketplaceCount,
    postActivities: postCount,
    userActivities: userCount
  };
};

// Force refresh activities from API
export const refreshActivities = async () => {
  activitiesLoaded = false;
  await loadActivities();
};

 